/**
 * Monthly timecard summary helpers for Listë Prezencat (approvals) reports.
 * Detail rows use Albanian column labels from getTranslation().
 */

export const SUMMARY_COLS = {
  employee: "Punonjësi",
  department: "Departamenti",
  team: "Kompania",
  before19: "Deri në 19",
  between19and22: "19:00 - 22:00",
  after22: "22:00 - 07:30",
  totalHours: "Orë pune",
  breakHours: "Zbritje pushimi (orë)",
  hoursWithBreak: "Orë me pushim",
};

const DETAIL_KEYS = {
  employee: ["Punonjësi"],
  department: ["Departamenti"],
  team: ["Kompania"],
  before19: ["Deri në 19"],
  between19and22: ["19:00 - 22:00"],
  after22: ["22:00 - 07:30"],
  duration: ["Kohëzgjatja"],
  durationWithBreak: ["Kohëzgjatja me pushim"],
  breakHours: ["Zbritje pushimi (orë)"],
};

const toNum = (v) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (v == null || v === "") return 0;
  const n = Number(
    String(v)
      .replace(/ALL/gi, "")
      .replace(/\s/g, "")
      .replace(/,/g, "")
      .replace(/[^\d.-]/g, ""),
  );
  return Number.isFinite(n) ? n : 0;
};

const pick = (row, keys) => {
  for (const k of keys) {
    if (row?.[k] != null && row[k] !== "") return row[k];
  }
  return "";
};

const round2 = (n) => parseFloat((Number(n) || 0).toFixed(2));

/**
 * One summary row per employee from detail timecard rows.
 * @param {Array<Record<string, unknown>>} detailRows
 * @returns {Array<Record<string, string|number>>}
 */
export function buildTimecardSummary(detailRows = []) {
  const byEmployee = new Map();

  for (const row of detailRows) {
    const name = String(pick(row, DETAIL_KEYS.employee) || "").trim();
    if (!name) continue;

    const before19 = toNum(pick(row, DETAIL_KEYS.before19));
    const between = toNum(pick(row, DETAIL_KEYS.between19and22));
    const after22 = toNum(pick(row, DETAIL_KEYS.after22));
    const duration = toNum(pick(row, DETAIL_KEYS.duration));
    const withBreak = toNum(pick(row, DETAIL_KEYS.durationWithBreak));
    let breakHours = toNum(pick(row, DETAIL_KEYS.breakHours));
    if (!breakHours && withBreak > duration) {
      breakHours = withBreak - duration;
    }

    const netHours =
      duration > 0 ? duration : before19 + between + after22;

    if (!byEmployee.has(name)) {
      byEmployee.set(name, {
        [SUMMARY_COLS.employee]: name,
        [SUMMARY_COLS.department]: String(
          pick(row, DETAIL_KEYS.department) || "",
        ).trim(),
        [SUMMARY_COLS.team]: String(pick(row, DETAIL_KEYS.team) || "").trim(),
        [SUMMARY_COLS.before19]: 0,
        [SUMMARY_COLS.between19and22]: 0,
        [SUMMARY_COLS.after22]: 0,
        [SUMMARY_COLS.totalHours]: 0,
        [SUMMARY_COLS.breakHours]: 0,
        [SUMMARY_COLS.hoursWithBreak]: 0,
      });
    }

    const agg = byEmployee.get(name);
    if (!agg[SUMMARY_COLS.department]) {
      agg[SUMMARY_COLS.department] = String(
        pick(row, DETAIL_KEYS.department) || "",
      ).trim();
    }
    if (!agg[SUMMARY_COLS.team]) {
      agg[SUMMARY_COLS.team] = String(pick(row, DETAIL_KEYS.team) || "").trim();
    }
    agg[SUMMARY_COLS.before19] += before19;
    agg[SUMMARY_COLS.between19and22] += between;
    agg[SUMMARY_COLS.after22] += after22;
    agg[SUMMARY_COLS.totalHours] += netHours;
    agg[SUMMARY_COLS.breakHours] += breakHours;
    agg[SUMMARY_COLS.hoursWithBreak] += withBreak > 0 ? withBreak : netHours;
  }

  return [...byEmployee.values()]
    .map((row) => ({
      ...row,
      [SUMMARY_COLS.before19]: round2(row[SUMMARY_COLS.before19]),
      [SUMMARY_COLS.between19and22]: round2(row[SUMMARY_COLS.between19and22]),
      [SUMMARY_COLS.after22]: round2(row[SUMMARY_COLS.after22]),
      [SUMMARY_COLS.totalHours]: round2(row[SUMMARY_COLS.totalHours]),
      [SUMMARY_COLS.breakHours]: round2(row[SUMMARY_COLS.breakHours]),
      [SUMMARY_COLS.hoursWithBreak]: round2(row[SUMMARY_COLS.hoursWithBreak]),
    }))
    .sort((a, b) =>
      String(a[SUMMARY_COLS.employee]).localeCompare(
        String(b[SUMMARY_COLS.employee]),
        "sq",
        { sensitivity: "base" },
      ),
    );
}

/**
 * Filter summary (and optionally detail) by department and/or team.
 * Empty arrays mean "no filter on that field".
 */
export function filterByDeptTeam(rows, { departments = [], teams = [] } = {}) {
  const deptSet = new Set((departments || []).filter(Boolean));
  const teamSet = new Set((teams || []).filter(Boolean));
  if (!deptSet.size && !teamSet.size) return rows;

  return (rows || []).filter((row) => {
    const dept = String(
      row[SUMMARY_COLS.department] ?? row.Departamenti ?? "",
    ).trim();
    const team = String(row[SUMMARY_COLS.team] ?? row.Kompania ?? "").trim();
    const deptOk = !deptSet.size || deptSet.has(dept);
    const teamOk = !teamSet.size || teamSet.has(team);
    return deptOk && teamOk;
  });
}

export function uniqueOptions(rows, key) {
  return [
    ...new Set(
      (rows || [])
        .map((r) => String(r?.[key] ?? "").trim())
        .filter(Boolean),
    ),
  ].sort((a, b) => a.localeCompare(b, "sq", { sensitivity: "base" }));
}
